import { FC, useCallback, useState } from 'react';
import { Badge } from '@shadcn/ui/components/ui/badge';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@shadcn/ui/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@shadcn/ui/components/ui/tooltip';
import { ChevronDown, Info } from 'lucide-react';
import { useMutation } from '@apollo/client';
import { UPDATE_CONTACT_TYPE } from '@zaplify/graphql';
import { Roles } from '@andsend/intelligence-shared';
import { cn } from '@shadcn/ui';

interface ContactTypeBadgeProps {
    contactId: string;
    contactType?: string | null;
    currentMetadata?: any;
    onContactTypeUpdated?: () => void;
}

export const ContactTypeBadge: FC<ContactTypeBadgeProps> = ({
    contactId,
    contactType,
    currentMetadata,
    onContactTypeUpdated,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [updateContactType, { loading }] = useMutation(UPDATE_CONTACT_TYPE, {
        onCompleted: () => {
            onContactTypeUpdated?.();
        },
        onError: (error) => {
            console.error('Failed to update contact type:', error);
        },
    });

    const formatContactType = useCallback((type: string) => {
        return type.charAt(0).toUpperCase() + type.toLowerCase().replace('_', ' ').slice(1);
    }, []);

    const handleContactTypeChange = async (newContactType: string, event?: React.MouseEvent) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        try {
            await updateContactType({
                variables: {
                    contactId,
                    contactType: newContactType,
                },
            });
        } catch (error) {
            console.error('Error updating contact type:', error);
        }
        setIsOpen(false);
    };

    const sortedContactTypes = [...Roles].sort((a, b) => a.localeCompare(b));

    if (!contactType) {
        return (
            <Tooltip delayDuration={500}>
                <TooltipTrigger asChild>
                    <Badge variant="outline" className="text-xs flex gap-1 items-center">
                        <Info className="w-3 h-3 flex-shrink-0" />
                        <span className="whitespace-nowrap">Not processed</span>
                    </Badge>
                </TooltipTrigger>
                <TooltipContent>
                    <p>This contact is waiting to be processed.</p>
                </TooltipContent>
            </Tooltip>
        );
    }

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                <Badge
                    variant="outline"
                    className="group/badge text-xs w-fit cursor-pointer hover:bg-gray-100 transition-colors flex items-center gap-1"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                >
                    <span>{formatContactType(contactType)}</span>
                    <ChevronDown
                        className={cn('w-0 h-3 flex-shrink-0 group-hover/badge:w-3 transition-[width] duration-200', {
                            'w-3': isOpen,
                        })}
                    />
                </Badge>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                className="max-w-52 text-ellipsis max-h-96 overflow-y-auto"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
            >
                {sortedContactTypes.map((role) => (
                    <DropdownMenuItem
                        key={role}
                        onClick={(e) => handleContactTypeChange(role, e)}
                        disabled={loading}
                        className={`p-2 text-ellipsis break-all cursor-pointer ${
                            role === contactType ? 'bg-background-brand-primary-subtle' : ''
                        }`}
                    >
                        {formatContactType(role)}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
